import React from 'react';
import { Form, Input, Button } from '@crisellevaldez/speculo-ui';
import { Info, Undo2 } from 'lucide-react';
import { Flow } from '../../../../hooks/useFlowsData';
import { FormattedTextarea } from '../../../common/FormattedTextarea';
import { capitalizeFirst } from '../../../../utils/strings';
import { Tooltip } from '../../../common/Tooltip';
import { ImproveWithAIButton } from '../ImproveWithAIButton';

// Flow-specific description placeholders
const DESCRIPTION_PLACEHOLDERS: Record<string, string> = {
  Convert: 'We are calling people who just requested more information on a property on our website',
  Revive:
    'We are calling people who reached out to us in the past about real estate in [insert primary client location like Columbus, Nashville, Tampa, etc.]',
  Engage:
    'We are calling valued clients to invite them to our {insert name of event} event. The call should focus on the details of the event, and on getting clients to rsvp. The event takes place on {insert month, date, "from" time}. It will be located at {insert location} (Make sure to enunciate the name of the location). What\'s included: {insert what is included} and so much more!',
};

interface FlowBasicInfoProps {
  flow: Flow;
  name: string;
  description: string;
  selectedDescription: string;
  nameError?: string;
  descriptionError?: string;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onSelectedDescriptionChange: (value: string) => void;
  /** Optional callback to save description immediately (e.g., after improving with AI) */
  onSaveDescription?: (improvedText: string) => Promise<unknown> | void;
  disabled?: {
    name?: boolean;
    description?: boolean;
  };
}

export const FlowBasicInfo: React.FC<FlowBasicInfoProps> = ({
  flow,
  name,
  description,
  nameError,
  descriptionError,
  onNameChange,
  onDescriptionChange,
  onSaveDescription,
  disabled,
}) => {
  return (
    <div className="space-y-6">
      <div className={flow.type === 'Engage' ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : ''}>
        <Form.Control>
          <Form.Label>Name</Form.Label>
          <Input
            placeholder="Enter flow name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            helperText="Internal Name for the Campaign"
            error={nameError}
            disabled={disabled?.name}
          />
        </Form.Control>
        {flow.type === 'Engage' && (
          <Form.Control>
            <Form.Label>Engage Type</Form.Label>
            <Input value={capitalizeFirst(flow.sub_type)} disabled />
          </Form.Control>
        )}
      </div>

      <div data-tour="edit-flow-description">
        <Form.Control>
          <div className="flex items-center gap-3 mb-2">
            <Form.Label className="mb-0">Description</Form.Label>
            <Tooltip
              content="Edit the description directly or click 'Improve Description' to enhance your description and make it more professional and clear."
              position="right"
            >
              <span className="group rounded-full transition-colors cursor-help flex items-center">
                <Info className="w-4 h-4 text-gray-400 group-hover:text-yellow-600" />
              </span>
            </Tooltip>
            <ImproveWithAIButton
              text={description}
              onImproved={onDescriptionChange}
              onSave={onSaveDescription}
              disabled={disabled?.description}
            />
            {description !== (flow.description || '') && !disabled?.description && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 h-8 px-2"
                onClick={() => onDescriptionChange(flow.description || '')}
                title="Revert to saved description"
              >
                <Undo2 className="w-3.5 h-3.5" />
                Revert
              </Button>
            )}
          </div>
          <FormattedTextarea
            value={description}
            onChange={onDescriptionChange}
            placeholder={
              DESCRIPTION_PLACEHOLDERS[flow.type] ||
              'Describe the purpose and context of this flow...'
            }
            error={descriptionError}
            readOnly={disabled?.description}
          />
        </Form.Control>
      </div>
    </div>
  );
};
